import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'

/**
 * The client's admission form (SUB-MVP-52). Loads the active template plus the
 * client's own draft, autosaves answers, and submits. Not persisted: re-fetched
 * on every visit so a stale draft never lingers. Mirrors ProfileStore's auth +
 * error handling.
 */
export const useIntakeStore = defineStore('intakeStore', () => {
  const authStore = useAuthStore()

  const template = ref(null)
  // field_key -> { value: string|null, client_document_id: number|null, file_name: string|null }
  const answers = ref({})
  const status = ref('draft')
  const submittedAt = ref(null)

  const loading = ref(false)
  const loaded = ref(false) // a first successful load has happened
  const loadError = ref(false)
  const noForm = ref(false) // 404: no active template

  const errors = ref({}) // field_key -> [messages] (422 from autosave)
  const uploadErrors = ref({}) // field_key -> [messages] (client-side upload failures, kept separate from PUT validation)
  const missingRequired = ref([]) // field_keys returned by submit's 422
  const saving = ref(false)
  const saved = ref(false)
  const saveError = ref(false)
  const submitting = ref(false)
  const submitError = ref(false)

  const fields = computed(() =>
    [...(template.value?.fields ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  )
  const isSubmitted = computed(() => status.value === 'submitted')

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  // Status/submitted_at are safe to take from any server reply. Answers are NOT:
  // rebinding them from an autosave echo would clobber keystrokes made while the
  // PUT was in flight (the server can only echo the stale payload it received).
  const applyStatus = (submission) => {
    if (!submission) return
    status.value = submission.status
    submittedAt.value = submission.submitted_at
  }

  // Full replace of the local answers from the server — only on load() and after
  // submit() (which freezes the form), never on autosave.
  const applySubmission = (submission) => {
    if (!submission) return
    applyStatus(submission)
    const next = {}
    for (const answer of submission.answers ?? []) {
      next[answer.field_key] = {
        value: answer.value,
        client_document_id: answer.client_document_id,
        file_name: answers.value[answer.field_key]?.file_name ?? null,
      }
    }
    answers.value = next
  }

  // Stale-while-revalidate: only show the blocking spinner on the first load.
  // A revisit keeps the cached form + draft on screen and refreshes silently
  // (the client isn't mid-typing at mount, so replacing answers is safe).
  const load = async () => {
    const spin = !loaded.value
    if (spin) loading.value = true
    loadError.value = false
    noForm.value = false
    try {
      const { data } = await axios.get('/api/client/intake', { headers: headers() })
      template.value = data.template
      applySubmission(data.submission)
      loaded.value = true
    } catch (error) {
      if (handleAuthError(error)) return
      if (error?.response?.status === 404) noForm.value = true
      else loadError.value = true
    } finally {
      if (spin) loading.value = false
    }
  }

  const answersPayload = () =>
    Object.entries(answers.value).map(([field_key, a]) => ({
      field_key,
      value: a.value ?? null,
      client_document_id: a.client_document_id ?? null,
    }))

  // Autosave: send the whole answer set (server upserts by field_key). Always
  // 200 on success; a 422 pins per-field errors; a 409 means it was already
  // submitted elsewhere, so re-sync.
  const save = async () => {
    if (isSubmitted.value) return false
    saving.value = true
    saved.value = false
    saveError.value = false
    try {
      const { data } = await axios.put(
        '/api/client/intake',
        { answers: answersPayload() },
        { headers: headers() }
      )
      // Only sync status — never rebind answers from the echo, or edits made
      // while this PUT was in flight would be lost. A clean 200 means the
      // fields the PUT carried all validated, so clear their validation errors.
      applyStatus(data.data)
      errors.value = {}
      saved.value = true
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      if (error?.response?.status === 422) errors.value = error.response.data?.errors ?? {}
      else if (error?.response?.status === 409) await load()
      else saveError.value = true
      return false
    } finally {
      saving.value = false
    }
  }

  const submit = async () => {
    submitting.value = true
    submitError.value = false
    missingRequired.value = []
    // Persist the latest edits first, so submit validates what the user sees.
    // If that save didn't land (validation OR a transport error), never finalize
    // on top of an unpersisted edit — stop and let the UI show the reason.
    const savedOk = await save()
    if (!savedOk) {
      submitting.value = false
      return false
    }
    try {
      const { data } = await axios.post('/api/client/intake/submit', {}, { headers: headers() })
      applySubmission(data.data)
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      if (error?.response?.status === 422) {
        missingRequired.value = error.response.data?.fields ?? []
      } else if (error?.response?.status === 409) {
        await load()
      } else {
        submitError.value = true
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  // Document-type field: upload to the client's document store, then hold the
  // returned id as this field's answer (the intake write validates ownership).
  const uploadDocument = async (fieldKey, file) => {
    // Upload failures live in their own ref, so an unrelated autosave never
    // wipes this field's "upload failed" message.
    uploadErrors.value = { ...uploadErrors.value, [fieldKey]: undefined }
    const form = new FormData()
    form.append('file', file)
    try {
      const { data } = await axios.post('/api/client/documents', form, {
        headers: { ...headers(), Accept: 'application/json' },
      })
      const doc = data.data ?? data
      answers.value[fieldKey] = {
        value: null,
        client_document_id: doc.id,
        file_name: doc.file_name ?? file.name,
      }
      clearMissing(fieldKey)
      await save()
      return true
    } catch (error) {
      if (handleAuthError(error)) return false
      const message =
        error?.response?.status === 422
          ? Object.values(error.response.data?.errors ?? {})[0]?.[0] ?? 'That file was rejected.'
          : 'Upload failed. Try again.'
      uploadErrors.value = { ...uploadErrors.value, [fieldKey]: [message] }
      return false
    }
  }

  // Drop a field from the required-missing cue as soon as it has a value, so the
  // red hint clears while the user fills the form (not only on the next submit).
  const clearMissing = (fieldKey) => {
    if (missingRequired.value.includes(fieldKey)) {
      missingRequired.value = missingRequired.value.filter((k) => k !== fieldKey)
    }
  }

  const setValue = (fieldKey, value) => {
    answers.value[fieldKey] = {
      value,
      client_document_id: answers.value[fieldKey]?.client_document_id ?? null,
      file_name: answers.value[fieldKey]?.file_name ?? null,
    }
    saved.value = false // the shown answer no longer matches what's persisted
    if (value) clearMissing(fieldKey)
  }

  const reset = () => {
    template.value = null
    answers.value = {}
    status.value = 'draft'
    submittedAt.value = null
    loading.value = false
    loaded.value = false
    loadError.value = false
    noForm.value = false
    errors.value = {}
    uploadErrors.value = {}
    missingRequired.value = []
    saving.value = false
    saved.value = false
    saveError.value = false
    submitting.value = false
    submitError.value = false
  }

  return {
    template,
    answers,
    status,
    submittedAt,
    loading,
    loadError,
    noForm,
    errors,
    uploadErrors,
    missingRequired,
    saving,
    saved,
    saveError,
    submitting,
    submitError,
    fields,
    isSubmitted,
    load,
    save,
    submit,
    uploadDocument,
    setValue,
    reset,
  }
})
